if (!customElements.get('pdp-sticky-add-to-cart')) {
  customElements.define('pdp-sticky-add-to-cart', class extends HTMLElement {
    connectedCallback() {
      this.main = document.querySelector('.pdp-main-product');
      this.mainButton = this.main?.querySelector('[data-add-to-cart]');
      this.mainForm = this.mainButton?.closest('form');
      this.price = this.querySelector('[data-sticky-price]');
      this.submit = this.querySelector('[data-sticky-submit]');
      this.toggle = this.querySelector('[data-sticky-extras-toggle]');
      this.panel = this.querySelector('[data-sticky-extras]');
      this.list = this.querySelector('[data-sticky-extras-list]');
      this.personalize = this.querySelector('[data-sticky-personalize]');
      this.isSubmitting = false;
      this.productAvailable = this.dataset.productAvailable === 'true';

      this.onAovChange = this.onAovChange.bind(this);
      this.onDocumentChange = this.onDocumentChange.bind(this);

      if (!this.mainButton || !this.mainForm || !this.submit) {
        this.remove();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        const visible = entries[0]?.isIntersecting;
        this.classList.toggle('is-visible', !visible);
        window.dispatchEvent(new CustomEvent('cardology:main-add-to-cart-visible', {
          detail: { isVisible: Boolean(visible) },
        }));
      }, { threshold: 0.15 });

      this.observer.observe(this.mainButton);
      this.submit.addEventListener('click', () => this.submitThroughCartController());
      this.toggle?.addEventListener('click', () => this.setOpen(!this.classList.contains('is-open')));
      this.personalize?.addEventListener('click', () => {
        const target = this.main.querySelector('.oe-personalizer') || document.querySelector('.oe-personalizer');
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      window.addEventListener('cardology:aov-addons-change', this.onAovChange);
      document.addEventListener('change', this.onDocumentChange);

      this.renderExtras();
      this.syncMainState();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('cardology:aov-addons-change', this.onAovChange);
      document.removeEventListener('change', this.onDocumentChange);
    }

    setLoading(isLoading) {
      this.isSubmitting = isLoading;
      this.submit?.classList.toggle('is-loading', isLoading);

      if (isLoading) {
        this.submit?.setAttribute('aria-busy', 'true');
        this.submit?.setAttribute('aria-label', 'Adding to basket');
      } else {
        this.submit?.removeAttribute('aria-busy');

        if (this.productAvailable && this.mainButton) {
          this.mainButton.disabled = false;
          this.mainButton.removeAttribute('aria-disabled');
        }
      }

      this.syncMainState();
    }

    showTemporaryLabel(message, duration = 1800) {
      const label = this.submit?.querySelector('[data-sticky-submit-label]');
      if (!label) return;

      label.textContent = message;
      window.setTimeout(() => this.syncMainState(), duration);
    }

    async submitThroughCartController() {
      if (
        this.isSubmitting ||
        !this.productAvailable ||
        !this.mainForm ||
        !this.mainButton
      ) return;

      if (typeof window.CardologyPdpCart?.submit !== 'function') {
        console.error('Cardology sticky ATC: shared cart controller is unavailable.');
        this.showTemporaryLabel('Please try again');
        return;
      }

      await window.CardologyPdpCart.submit(this.mainForm, this.mainButton, { source: 'sticky' });
    }

    setOpen(open) {
      this.classList.toggle('is-open', open);
      if (this.panel) this.panel.hidden = !open;
      this.toggle?.setAttribute('aria-expanded', String(open));
    }

    syncMainState() {
      if (!this.submit) return;

      const disabled = this.isSubmitting || !this.productAvailable;
      this.submit.disabled = disabled;
      this.submit.toggleAttribute('aria-disabled', disabled);

      const label = this.submit.querySelector('[data-sticky-submit-label]');
      if (label && !this.isSubmitting) {
        label.textContent = this.productAvailable
          ? (this.dataset.addLabel || 'Add to Basket')
          : (this.dataset.soldOutLabel || 'Sold out');
      }

      if (!this.isSubmitting) {
        this.submit.setAttribute(
          'aria-label',
          this.productAvailable
            ? (this.dataset.addLabel || 'Add to Basket')
            : (this.dataset.soldOutLabel || 'Sold out')
        );
      }

      const sourcePrice = this.main?.querySelector('[data-pdp-button-price]')?.textContent?.trim();
      if (sourcePrice && this.price) this.price.textContent = sourcePrice;
    }

    renderExtras() {
      if (!this.list) return;

      const sourceItems = [...document.querySelectorAll('pdp-aov-boaster [data-addon]')];
      this.list.innerHTML = '';

      sourceItems.forEach((source) => {
        const item = document.createElement('div');
        item.className = 'pdp-sticky-atc__extra';
        item.dataset.addonId = source.dataset.addonId || '';

        const image = source.querySelector('img')?.cloneNode(true);
        const imageWrap = document.createElement('span');
        imageWrap.className = 'pdp-sticky-atc__extra-image';
        if (image) imageWrap.appendChild(image);

        const copy = document.createElement('span');
        copy.className = 'pdp-sticky-atc__extra-copy';
        copy.innerHTML = `<span class="pdp-sticky-atc__extra-name">${source.dataset.addonName || ''}</span><span class="pdp-sticky-atc__extra-price">${source.querySelector('.pdp-aov-boaster__price')?.textContent?.trim() || ''}</span>`;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pdp-sticky-atc__extra-button';
        button.textContent = this.dataset.extraAddLabel || 'Add';
        button.disabled = source.disabled;
        button.addEventListener('click', () => {
          source.click();
          this.syncExtraItem(item, source);
        });

        item.append(imageWrap, copy, button);
        this.syncExtraItem(item, source);
        this.list.appendChild(item);
      });

      if (this.toggle) this.toggle.hidden = sourceItems.length === 0;
    }

    syncExtraItem(item, source) {
      const selected = source.classList.contains('is-selected');
      item.classList.toggle('is-selected', selected);

      const button = item.querySelector('.pdp-sticky-atc__extra-button');
      if (button) {
        button.textContent = selected
          ? (this.dataset.extraRemoveLabel || 'Remove')
          : (this.dataset.extraAddLabel || 'Add');
      }
    }

    onAovChange() {
      const sourceItems = [...document.querySelectorAll('pdp-aov-boaster [data-addon]')];
      [...this.querySelectorAll('.pdp-sticky-atc__extra')].forEach((item) => {
        const source = sourceItems.find((node) => node.dataset.addonId === item.dataset.addonId);
        if (source) this.syncExtraItem(item, source);
      });
    }

    onDocumentChange() {
      requestAnimationFrame(() => this.syncMainState());
    }
  });
}
